import React, { Dispatch, SetStateAction, memo, useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { DerivedValue, SharedValue } from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { ReanimatedAccordion } from '@/components/ReanimatedAccordion';
import { Box } from '@/components/lib/components';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { isProjectionSelected } from '@/utils/projections';

import { AlternateProjection } from '../AlternateProjection';

export const AlternateProjectionRowExpandedContent = memo(
    ({
        setActiveAlternateIndex,
        activeAlternateIndex,
        expanded,
        projectionsGroup,
        rowIndex,
        expandedIndex,
        playerPicked,
    }: {
        setActiveAlternateIndex: Dispatch<SetStateAction<number>>;
        activeAlternateIndex: number;
        expanded: DerivedValue<boolean>;
        projectionsGroup: PlayerProjectionFragment[];
        rowIndex: number;
        expandedIndex: SharedValue<number>;
        playerPicked?: BetslipPick;
    }) => {
        const scrollRef = useRef<FlatList>(null);
        const onViewableItemsChanged = useCallback(() => {
            // this is a workaround for the FlatList to scroll to the selected item
            // when the user opens the accordion
            // as initialScrollIndex was causing the list to disappear
            if (expandedIndex.value === null || expandedIndex.value !== rowIndex) {
                scrollRef.current?.scrollToIndex({
                    index: activeAlternateIndex,
                    animated: true,
                    viewPosition: 0.5, // centers the selected item
                    viewOffset: 0,
                });
            }
        }, [activeAlternateIndex, expandedIndex.value, rowIndex]);
        const viewabilityConfigCallbackPairs = useRef([
            // there's a crash on web if we don't use a ref for the viewabilityConfigCallbackPairs
            // viewAreaCoveragePercentThreshold: 10% of the item needs to be visible so that the item is considered viewable
            { onViewableItemsChanged, viewabilityConfig: { viewAreaCoveragePercentThreshold: 10 } },
        ]);
        return (
            <ReanimatedAccordion expanded={expanded} contentHeight={56}>
                <Box pt={'s4'} pb={'s16'} justifyContent={'center'}>
                    <Box pointerEvents={'none'} zIndex={1}>
                        <LinearGradient
                            colors={[designSystem.colors.neutral800, withOpacity(designSystem.colors.neutral800, 0)]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            width={24}
                            height={36}
                            style={styles.gradient}
                        />
                        <LinearGradient
                            colors={[designSystem.colors.neutral800, withOpacity(designSystem.colors.neutral800, 0)]}
                            start={{ x: 1, y: 0 }}
                            end={{ x: 0, y: 0 }}
                            width={24}
                            height={36}
                            style={[styles.gradient, styles.right]}
                        />
                    </Box>
                    {projectionsGroup.length > 0 ? (
                        <FlatList<PlayerProjectionFragment>
                            keyExtractor={(item, pIndex) => `${pIndex}-${item.marketId}`}
                            data={projectionsGroup}
                            viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs.current}
                            onScrollToIndexFailed={error => {
                                // if the scrollToIndex fails, we scroll to the furthest offset
                                scrollRef.current?.scrollToOffset({
                                    offset: error.averageItemLength * error.index,
                                    animated: true,
                                });
                            }}
                            renderItem={({ item: alternateProjection, index: pIndex }) => {
                                const isSelected = isProjectionSelected(alternateProjection, playerPicked);
                                const showActiveDot = isSelected && activeAlternateIndex !== pIndex;
                                return (
                                    <AlternateProjection
                                        showActiveDot={showActiveDot}
                                        alternateProjection={alternateProjection}
                                        isActive={activeAlternateIndex === pIndex}
                                        setActiveAlternateIndex={() => {
                                            setActiveAlternateIndex(pIndex);
                                            scrollRef.current?.scrollToIndex({
                                                index: pIndex,
                                                animated: true,
                                                viewPosition: 0.5, // centers the selected item
                                                viewOffset: 0,
                                            });
                                        }}
                                    />
                                );
                            }}
                            ref={scrollRef}
                            horizontal
                            contentContainerStyle={[common.paddingHorizontal, styles.listContent]}
                            showsHorizontalScrollIndicator={showScrollIndicator}
                        />
                    ) : null}
                </Box>
            </ReanimatedAccordion>
        );
    }
);

const styles = StyleSheet.create({
    gradient: {
        position: 'absolute',
        width: 24,
        height: 36,
        zIndex: 1,
    },
    right: {
        right: 0,
    },
    alternateProjectionCard: { paddingHorizontal: 13, paddingVertical: 7, marginHorizontal: 5 },
    listContent: { gap: 10 },
});
